// How step types are NAMED across the canvas surfaces, resolved from the
// step-UI registry (step-ui-registry.tsx) so the wording stays identical
// everywhere — the action-type dropdown, the node cards, and the read-only
// text summary all call through here.
//
// The catalog ships unscoped backend labels (e.g. "Move Stage"); a registry
// entry's `label` presents the friendlier, subject-adapting name ("Update
// stage", "Create Project"). `{subject}` is replaced with the trigger's source
// noun ("Project") at render time, keeping the noun's own capital — the app
// writes its entity nouns as names ("Archive Project").
import { STEP_UI } from "#app/components/automations/canvas/step-ui-registry";

// `{subject}` → the trigger's noun as the app spells it — capitalized, wherever
// it lands in the label ("Create Project", "Send Project message").
//
// Exported on its own because a PROVIDER action's label needs the same
// treatment and has no step-UI entry to look up — it is keyed by an action key,
// not a step type, so passing it through `stepDisplayLabel` would search the
// registry with a key that can never be in it.
export function applySubject(label: string, subject: string): string {
  return label.replace("{subject}", subject);
}

// The display label for a step type: the registry override when one exists,
// else the catalog label, with `{subject}` resolved.
export function stepDisplayLabel(
  key: string,
  catalogLabel: string,
  subject: string,
): string {
  return applySubject(STEP_UI[key]?.label ?? catalogLabel, subject);
}

// Adapt a catalog description to the automation's subject. The server ships
// generic copy that names the primary entity "project" (e.g. "Archive the
// project."); the canvas presents it in the trigger's actual noun — the same
// server-generic → client-adapted split the labels use. Matches the whole word
// only (so "projects" pluralizes correctly and unrelated text is untouched); a
// Project automation is a no-op.
export function stepDescription(
  catalogDescription: string,
  subject: string,
): string {
  const noun = subject.toLowerCase();
  return catalogDescription
    .replace(/\bprojects\b/g, `${noun}s`)
    .replace(/\bproject\b/g, noun);
}

// The preposition that joins an action to its primary reference in the text
// summary, so it reads as a phrase ("Update stage" → to → "Won") — the
// registry entry's `connective`. Actions without one (e.g. "Update custom
// field <field>", "Create Project <workflow>") read as the label followed by
// the reference directly.
export function actionRefConnective(key: string): string | null {
  return STEP_UI[key]?.connective ?? null;
}
